import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PipelineFlowNodeComponent } from '@shared-lib/global-components/pipeline-flow-node/pipeline-flow-node.component';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import {
  ChartWrapperInstance,
  ChartWrapperService,
} from '@shared-lib/shared-services/chart-wrapper.service';
import {
  NodeType,
  WorkflowNode,
} from '@shared-lib/shared-services/pipeline-workflow.model';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { ThemeService } from '@shared-lib/shared-services/theme.service';

import { DockStateService } from '../../shared-services/dock-state.service';
import { IoWorkflowBuilderDataService } from './io-workflow-builder-data.service';
import { IoWorkflowDetailComponent } from './io-workflow-detail.component';
import {
  IOWorkflow,
  IoWorkflowBuilderSeedData,
  IoWorkflowBuilderWidgetConfig,
  IoWorkflowDetailWidgetData,
  SinkType,
  SourceType,
  WFType,
} from './io-workflow-builder.models';

const DEFAULT_CONFIG: Required<
  Pick<
    IoWorkflowBuilderWidgetConfig,
    'storageKey' | 'screenTitle' | 'chainTitle' | 'pipelineLabel' | 'dockSide' | 'components'
  >
> = {
  storageKey: 'io_workflows_chain_dvm',
  screenTitle: 'I/O Workflows',
  chainTitle: 'I/O Pipeline Chain',
  pipelineLabel: 'Pipeline Chain',
  dockSide: 'left',
  components: {
    flowNode: {
      type: 'pipeline-flow-node',
    },
    detail: {
      type: 'io-workflow-detail',
    },
  },
};

@Component({
  selector: 'app-io-workflow-builder',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    CdkDragPreview,
    PipelineFlowNodeComponent,
    IoWorkflowDetailComponent,
  ],
  templateUrl: './io-workflow-builder.component.html',
  styleUrl: './io-workflow-builder.component.scss',
})
export class IoWorkflowBuilderComponent implements OnInit, OnDestroy {
  @Input() set data(value: IoWorkflowBuilderWidgetConfig | null | undefined) {
    const nextConfig: IoWorkflowBuilderWidgetConfig = {
      ...DEFAULT_CONFIG,
      ...(value ?? {}),
      components: {
        ...DEFAULT_CONFIG.components,
        ...(value?.components ?? {}),
      },
    };
    const nextSignature = this.getConfigSignature(nextConfig);
    const configChanged = nextSignature !== this.widgetConfigSignature;

    this.widgetConfig = nextConfig;

    if (!configChanged) {
      return;
    }

    this.widgetConfigSignature = nextSignature;

    if (this.initialized) {
      this.loadSeedData();
    }

    this.updateThemeStyles();
    this.updateResponsiveState();
    this.wrapper.updateData(this.widgetConfig);
    this.refreshSelectedWorkflowDetailData();

    if (this.initialized) {
      this.markForCheck();
    }
  }

  workflows: IOWorkflow[] = [];
  selectedId: string | null = null;
  dockSide: 'left' | 'right' = 'left';
  screenTitle = DEFAULT_CONFIG.screenTitle;
  chainTitle = DEFAULT_CONFIG.chainTitle;
  pipelineLabel = DEFAULT_CONFIG.pipelineLabel;

  showAddModal = false;
  newWF = this.createNewWorkflowDraft();

  showSearchDropdown = false;
  searchQuery = '';
  mockRegistry: string[] = [];

  toastVisible = false;
  toastMessage = '';
  saveStatus: 'idle' | 'saving' | 'saved' = 'idle';
  isLoading = true;
  screenMode: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  rootStyles: Record<string, string> = {};
  selectedWorkflowDetailData: IoWorkflowDetailWidgetData | null = null;

  readonly SOURCE_TYPES: SourceType[] = ['SFTP_SOURCE', 'DB_SOURCE', 'API_SOURCE'];
  readonly SINK_TYPES: SinkType[] = ['GRPC_SINK', 'API_SINK', 'CSV_SINK'];

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dataService = inject(IoWorkflowBuilderDataService);
  private readonly dockState = inject(DockStateService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly themeService = inject(ThemeService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private readonly wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private widgetConfig: IoWorkflowBuilderWidgetConfig = { ...DEFAULT_CONFIG };
  private widgetConfigSignature = '';
  private initialized = false;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private saveStatusTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.initialized = true;
    this.attachWrapper();
    this.updateThemeStyles();
    this.updateResponsiveState();
    this.loadSeedData();

    this.dockState.side$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((side) => {
        this.dockSide = side;
        this.markForCheck();
      });

    this.responsiveLayoutService.reflow$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateResponsiveState();
        this.markForCheck();
      });

    this.themeService.theme$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateThemeStyles();
        this.markForCheck();
      });
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    if (this.saveStatusTimer) {
      clearTimeout(this.saveStatusTimer);
      this.saveStatusTimer = null;
    }

    this.wrapper.detach();
  }

  get filteredRegistry(): string[] {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) return this.mockRegistry;
    return this.mockRegistry.filter((name) => name.toLowerCase().includes(query));
  }

  get chainPrevId(): string {
    return this.workflows[0]?.previousWFId || '';
  }

  get chainNextId(): string {
    return this.workflows[this.workflows.length - 1]?.nextWFId || '';
  }

  get useSharedFlowNode(): boolean {
    return this.widgetConfig.components?.flowNode?.type === 'pipeline-flow-node';
  }

  get useWorkflowDetail(): boolean {
    return this.widgetConfig.components?.detail?.type === 'io-workflow-detail';
  }

  selectWorkflow(id: string): void {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this.refreshSelectedWorkflowDetailData();
    this.markForCheck();
  }

  dropWorkflow(event: CdkDragDrop<IOWorkflow[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const reordered = [...this.workflows];
    const [moved] = reordered.splice(event.previousIndex, 1);
    reordered.splice(event.currentIndex, 0, moved);
    this.workflows = reordered;
    this.persist();
  }

  openAddModal(): void {
    this.showAddModal = true;
    this.newWF = this.createNewWorkflowDraft();
    this.markForCheck();
  }

  closeAddModal(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.showAddModal = false;
    }
  }

  confirmAddWorkflow(): void {
    if (!this.newWF.workflowName.trim()) return;

    const workflow = this.buildWorkflow({
      workflowName: this.newWF.workflowName.trim(),
      entityName: this.newWF.entityName.trim(),
      fileName: this.newWF.entityName.trim().toLowerCase().replace(/ /g, '_'),
      type: this.newWF.type,
      sourceType: this.newWF.sourceType,
      sinkType: this.newWF.sinkType,
    });

    this.workflows = [...this.workflows, workflow];
    this.selectedId = workflow.id;
    this.refreshSelectedWorkflowDetailData();
    this.showAddModal = false;
    this.showToast(`"${workflow.workflowName}" added to pipeline chain`);
    this.persist();
  }

  deleteWorkflow(id: string): void {
    this.workflows = this.workflows.filter((workflow) => workflow.id !== id);

    if (this.selectedId === id) {
      this.selectedId = this.workflows[0]?.id ?? null;
    }

    if (!this.workflows.length) {
      this.chartFilterHandler.resetFilterSelection(this.widgetConfig);
    }

    this.refreshSelectedWorkflowDetailData();
    this.showToast('Workflow removed from chain');
    this.persist();
  }

  duplicateWorkflow(id: string): void {
    const original = this.workflows.find((workflow) => workflow.id === id);
    if (!original) return;

    const copy = JSON.parse(JSON.stringify(original)) as IOWorkflow;
    copy.id = this.createId('wf');
    copy.workflowName = `${original.workflowName} (copy)`;
    copy.status = 'DRAFT';

    this.workflows = [...this.workflows, copy];
    this.selectedId = copy.id;
    this.refreshSelectedWorkflowDetailData();
    this.showToast('Workflow duplicated');
    this.persist();
  }

  loadFromRegistry(name: string): void {
    this.showSearchDropdown = false;
    this.searchQuery = '';

    if (this.workflows.some((workflow) => workflow.workflowName === name)) {
      this.showToast(`"${name}" is already in the chain`);
      return;
    }

    const workflow = this.buildWorkflow({
      workflowName: name,
      entityName: 'entity_placeholder',
      fileName: name.toLowerCase().replace(/ /g, '_'),
      type: 'INBOUND',
      sourceType: 'SFTP_SOURCE',
      sinkType: 'GRPC_SINK',
    });

    this.workflows = [...this.workflows, workflow];
    this.selectedId = workflow.id;
    this.refreshSelectedWorkflowDetailData();
    this.showToast(`"${name}" loaded from registry`);
    this.persist();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.wf-search-wrap')) {
      this.showSearchDropdown = false;
    }
  }

  validate(): void {
    const valid = this.workflows.every((workflow) => workflow.isValid);
    this.showToast(valid ? 'All pipeline workflows are valid' : 'Some workflows have warnings');
  }

  save(): void {
    if (this.saveStatus === 'saving') return;

    if (this.saveStatusTimer) {
      clearTimeout(this.saveStatusTimer);
      this.saveStatusTimer = null;
    }

    this.saveStatus = 'saving';
    this.markForCheck();
    setTimeout(() => {
      this.persist();
      this.saveStatus = 'saved';
      this.showToast('Pipeline configuration saved');
      this.markForCheck();
      this.saveStatusTimer = setTimeout(() => {
        this.saveStatus = 'idle';
        this.saveStatusTimer = null;
        this.markForCheck();
      }, 3000);
    }, 900);
  }

  goBack(): void {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  }

  sourceSinkLabel(workflow: IOWorkflow): string {
    const sourceMap: Record<SourceType, string> = {
      SFTP_SOURCE: 'SFTP',
      DB_SOURCE: 'DB',
      API_SOURCE: 'API',
    };
    const sinkMap: Record<SinkType, string> = {
      GRPC_SINK: 'GRPC',
      API_SINK: 'API',
      CSV_SINK: 'CSV',
    };
    return `${sourceMap[workflow.sourceType]} -> ${sinkMap[workflow.sinkType]}`;
  }

  buildFlowNodeData(workflow: IOWorkflow, index: number): {
    node: WorkflowNode;
    index: number;
    isActive: boolean;
    displayType: NodeType;
    badgeLabel: string;
    label: string;
    metaChips: string[];
    statusLabel: string;
    statusClass: string;
    onNodeClick: (nodeId: string) => void;
    onInsertBefore: () => void;
  } {
    const displayType = this.resolveDisplayType(workflow);

    return {
      node: this.createFlowNode(workflow, displayType),
      index,
      isActive: workflow.id === this.selectedId,
      displayType,
      badgeLabel: workflow.type,
      label: workflow.workflowName,
      metaChips: this.buildMetaChips(workflow),
      statusLabel: workflow.isValid ? 'OK' : 'WARN',
      statusClass: workflow.isValid ? 'chip-valid' : 'chip-warning',
      onNodeClick: (nodeId: string) => this.handleWorkflowNodeClick(nodeId),
      onInsertBefore: () => this.openAddModal(),
    };
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  persist(): void {
    this.dataService.persist(
      this.widgetConfig.storageKey || DEFAULT_CONFIG.storageKey,
      this.workflows,
    );
    this.markForCheck();
  }

  private loadSeedData(): void {
    this.isLoading = true;
    this.markForCheck();
    this.screenTitle = this.widgetConfig.screenTitle || DEFAULT_CONFIG.screenTitle;
    this.chainTitle = this.widgetConfig.chainTitle || DEFAULT_CONFIG.chainTitle;
    this.pipelineLabel = this.widgetConfig.pipelineLabel || DEFAULT_CONFIG.pipelineLabel;
    this.dockState.setSide(this.widgetConfig.dockSide || DEFAULT_CONFIG.dockSide);

    this.dataService
      .loadSeed(this.widgetConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (seed) => this.applySeed(seed),
        error: () => {
          this.isLoading = false;
          this.markForCheck();
        },
      });
  }

  private applySeed(seed: IoWorkflowBuilderSeedData): void {
    this.screenTitle = seed.screenTitle || this.screenTitle;
    this.chainTitle = seed.chainTitle || this.chainTitle;
    this.pipelineLabel = seed.pipelineLabel || this.pipelineLabel;
    this.mockRegistry = [...seed.registry];
    this.workflows = seed.workflows;
    this.selectedId = this.workflows[0]?.id ?? null;
    this.isLoading = false;
    this.refreshSelectedWorkflowDetailData();
    this.markForCheck();
  }

  private createNewWorkflowDraft(): {
    workflowName: string;
    entityName: string;
    type: WFType;
    sourceType: SourceType;
    sinkType: SinkType;
  } {
    return {
      workflowName: '',
      entityName: '',
      type: 'INBOUND',
      sourceType: 'SFTP_SOURCE',
      sinkType: 'GRPC_SINK',
    };
  }

  private buildWorkflow(input: {
    workflowName: string;
    entityName: string;
    fileName: string;
    type: WFType;
    sourceType: SourceType;
    sinkType: SinkType;
  }): IOWorkflow {
    return {
      id: this.createId('wf'),
      workflowName: input.workflowName,
      entityName: input.entityName,
      fileName: input.fileName,
      type: input.type,
      status: 'DRAFT',
      sourceType: input.sourceType,
      sinkType: input.sinkType,
      sftp_hostname: '',
      sftp_port: 22,
      sftp_username: '',
      sftp_enabled: false,
      sftp_remotePath: '',
      sftp_localDir: '',
      sftp_privateKey: '',
      sftp_passphrase: '',
      sftp_splitSize: 1000,
      db_entityName: '',
      db_filterName: '',
      db_parameters: [],
      api_endpoint: '',
      api_dataPath: '',
      api_parameters: [],
      sink_apiUrl: '',
      sink_csvPath: '',
      sink_encryptKey: '',
      serviceEndpoints: [],
      ttmEnabled: false,
      sourceMetadata: [],
      targetMetadata: [],
      globalFilters: [],
      samayEnabled: false,
      startTime: '',
      pollTime: '600000',
      pollInterval: '30000',
      crudOperation: '',
      businessKeys: [],
      previousWFId: '',
      nextWFId: '',
      isValid: false,
    };
  }

  private resolveDisplayType(workflow: IOWorkflow): NodeType {
    return workflow.type === 'INBOUND' ? 'PARALLEL' : 'HIERARCHY';
  }

  private createFlowNode(workflow: IOWorkflow, displayType: NodeType): WorkflowNode {
    return {
      id: workflow.id,
      type: displayType,
      label: workflow.workflowName,
      isValid: workflow.isValid,
      hasWarning: !workflow.isValid,
      triggerConditions: workflow.sourceMetadata.map((field, index) => ({
        id: field.id || `tc_${workflow.id}_${index}`,
        field: field.columnName,
        operator: '',
        value: field.dataType,
      })),
      approvers: workflow.serviceEndpoints.map((endpoint) => ({
        id: endpoint.id,
        name: endpoint.functionName || endpoint.version,
        type: 'role',
      })),
      decisionRule: {
        type: 'ANY_ONE',
        description: this.sourceSinkLabel(workflow),
      },
      delegationUsers: [],
      sla: {
        reminderHours: 0,
        maxEscalations: 0,
        onTimeout: 'ESCALATE',
        escalationChain: [],
      },
    };
  }

  private buildMetaChips(workflow: IOWorkflow): string[] {
    const chips = [
      this.sourceSinkLabel(workflow),
      workflow.entityName,
      workflow.samayEnabled ? this.msToHuman(workflow.pollTime) : '',
      workflow.status,
    ];

    return chips.filter((chip): chip is string => Boolean(chip?.trim()));
  }

  private msToHuman(ms: string): string {
    const numeric = parseInt(ms, 10);
    if (Number.isNaN(numeric)) return '';
    if (numeric >= 3600000) return `${(numeric / 3600000).toFixed(1)}h`;
    if (numeric >= 60000) return `${Math.round(numeric / 60000)}m`;
    return `${Math.round(numeric / 1000)}s`;
  }

  private createId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  }

  private showToast(message: string): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastMessage = message;
    this.toastVisible = true;
    this.markForCheck();
    this.toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.toastTimer = null;
       this.markForCheck();
    }, 3000);
  }

  private attachWrapper(): void {
    this.wrapper.detach();
    this.wrapper.attach(this.widgetConfig, {
      onReload: () => this.loadSeedData(),
      getChart: () => null,
      getSeriesNames: () => this.workflows.map((workflow) => workflow.workflowName),
      getCategories: () => this.workflows.map((workflow) => workflow.workflowName),
    });
  }

  private handleWorkflowNodeClick(nodeId: string): void {
    this.selectWorkflow(nodeId);

    if (!this.widgetConfig.enableLabelClick) {
      return;
    }

    const workflow = this.workflows.find((item) => item.id === nodeId);
    if (!workflow) {
      return;
    }

    this.wrapper.handleLabelClick(workflow.workflowName);
  }

  private refreshSelectedWorkflowDetailData(): void {
    const workflow = this.workflows.find((item) => item.id === this.selectedId);
    this.selectedWorkflowDetailData = workflow && this.useWorkflowDetail
      ? {
          workflow,
          onPersist: () => this.persist(),
          onDeleteWorkflow: (workflowId: string) => this.deleteWorkflow(workflowId),
          onDuplicateWorkflow: (workflowId: string) => this.duplicateWorkflow(workflowId),
        }
      : null;
  }

  private updateResponsiveState(): void {
    if (typeof window === 'undefined') {
      this.screenMode = 'desktop';
      return;
    }

    const width = window.innerWidth;
    this.screenMode = width < 768 ? 'mobile' : width < 1280 ? 'tablet' : 'desktop';
  }

  private updateThemeStyles(): void {
    const theme = this.themeService.resolve(this.widgetConfig.themeOverrides);
    const accent = theme.chart?.palette?.[0] || '#043D63';
    const accentSoft = theme.chart?.palette?.[2] || '#07527d';
    const success = theme.chart?.palette?.[3] || '#059669';
    const warning = theme.chart?.palette?.[4] || '#d97706';
    const danger = theme.chart?.palette?.[1] || '#dc2626';
    const cardBg = theme.card?.bg || '#ffffff';
    const cardBodyBg = theme.card?.bodyBg || '#f8fbff';
    const headerBg = theme.card?.headerBg || '#f7fafd';
    const contentBg = theme.layout?.content?.bg || '#f5f5f0';
    const textColor = theme.layout?.content?.text || '#162033';
    const mutedText = theme.filter?.text || '#6b7280';
    const borderColor = theme.card?.borderColor || 'rgba(203, 213, 225, 0.9)';

    const resolvedHeight = this.responsiveLayoutService.resolveWidgetHeight(this.widgetConfig);

    this.rootStyles = {
      '--io-accent': accent,
      '--io-accent-soft': accentSoft,
      '--io-accent-faint': this.withAlpha(accent, 0.12),
      '--io-success': success,
      '--io-warning': warning,
      '--io-danger': danger,
      '--io-surface': cardBg,
      '--io-surface-soft': cardBodyBg,
      '--io-surface-muted': headerBg,
      '--io-page-bg': contentBg,
      '--io-text': textColor,
      '--io-text-muted': mutedText,
      '--io-text-soft': this.withAlpha(textColor, 0.64),
      '--io-border': borderColor,
      '--io-border-strong': this.withAlpha(accent, 0.22),
      '--io-shadow-soft': this.withAlpha(textColor, 0.08),
      '--io-shadow-deep': this.withAlpha(textColor, 0.14),
      '--io-toolbar-bg': theme.layout?.toolbar?.bg || cardBg,
      '--io-toolbar-text': theme.layout?.toolbar?.text || textColor,
      '--io-filter-hover': theme.legend?.hoverBg || this.withAlpha(accent, 0.08),
      '--io-button-text': theme.button?.primaryText || '#ffffff',
      '--io-widget-height': resolvedHeight,
    };
  }

  private withAlpha(color: string, alpha: number): string {
    const normalized = color.trim();
    if (normalized.startsWith('#')) {
      const hex = normalized.slice(1);
      const expanded = hex.length === 3
        ? hex.split('').map((char) => char + char).join('')
        : hex;

      if (/^[0-9a-fA-F]{6}$/.test(expanded)) {
        const red = parseInt(expanded.slice(0, 2), 16);
        const green = parseInt(expanded.slice(2, 4), 16);
        const blue = parseInt(expanded.slice(4, 6), 16);
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
      }
    }

    return color;
  }

  private markForCheck(): void {
    this.cdr.markForCheck();
  }

  private getConfigSignature(config: IoWorkflowBuilderWidgetConfig): string {
    try {
      return JSON.stringify(config);
    } catch {
      return [
        config.widgetId ?? '',
        config.dataFile ?? '',
        config.storageKey ?? '',
        config.screenTitle ?? '',
        config.chainTitle ?? '',
        config.pipelineLabel ?? '',
        config.dockSide ?? '',
        config.height ?? '',
      ].join('|');
    }
  }
}
